import React, { useState, useMemo } from "react"
import { Helmet } from "react-helmet-async"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Button,
  Paper,
  Container,
} from "@mui/material"

// Material UI Icons
import SportsEsportsIcon from "@mui/icons-material/SportsEsports"
import SportsBaseballIcon from "@mui/icons-material/SportsBaseball"
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked"

import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff"
import ChurchIcon from "@mui/icons-material/Church"
import AirplanemodeActiveIcon from "@mui/icons-material/AirplanemodeActive"
import MapIcon from "@mui/icons-material/Map"
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat"

import CleaningServicesIcon from "@mui/icons-material/CleaningServices"
import FavoriteIcon from "@mui/icons-material/Favorite"
import TimelineIcon from "@mui/icons-material/Timeline"
import NewspaperIcon from "@mui/icons-material/Newspaper"
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart"

import PublicIcon from "@mui/icons-material/Public"
import AccountTreeIcon from "@mui/icons-material/AccountTree"
import AutoStoriesIcon from "@mui/icons-material/AutoStories"

import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"

import SiteWrapper from "./SiteWrapper"

const CATEGORIES = {
  GAMES: "games",
  TRAVEL: "travel",
  FAMILY: "family",
  CHURCH: "church",
}

const NewHomePage = () => {
  const navigate = useNavigate()
  const [activeCategory, setActiveCategory] = useState(null)

  // Newsletter path calculation
  const currentNewsLetterPath = useMemo(() => {
    const currentDate = new Date()
    currentDate.setMonth(currentDate.getMonth() - 1)
    const year = String(currentDate.getFullYear())
    const month = currentDate.toLocaleString("default", { month: "long" })
    return `/newsletter/${year}/${month}`
  }, [])

  // Sub-pages definition mapping to categories
  const subPages = {
    [CATEGORIES.GAMES]: [
      {
        title: "Buzzer",
        desc: "Real-time game show buzzer. Host a session or join as a player.",
        icon: RadioButtonCheckedIcon,
        path: "/buzzer",
        glow: "rgba(239, 68, 68, 0.4)", // red
        badge: "Party Game",
      },
      {
        title: "Scattergories",
        desc: "Fast-paced word game. Spin the wheel and match categories.",
        icon: SportsEsportsIcon,
        path: "/scattergories",
        glow: "rgba(99, 102, 241, 0.4)", // indigo
        badge: "Family Classic",
      },
      {
        title: "Just One",
        desc: "Cooperative word guessing game. Give unique one-word clues and avoid duplicates!",
        icon: SportsEsportsIcon,
        path: "/justone",
        glow: "rgba(6, 182, 212, 0.4)", // cyan
        badge: "Co-op Fun",
      },
      {
        title: "Ballpark",
        desc: "Scoreboard and lineup tracker for the backyard ball game.",
        icon: SportsBaseballIcon,
        path: "/ballpark",
        glow: "rgba(34, 197, 94, 0.4)", // green
        badge: "Backyard Fun",
      },
    ],
    [CATEGORIES.TRAVEL]: [
      {
        title: "Travel Log",
        desc: "Preserve the family travel logs, visited states, and maps.",
        icon: MapIcon,
        path: "/travel",
        glow: "rgba(16, 185, 129, 0.4)", // emerald
        badge: "Interactive Map",
      },
      {
        title: "Non Rev",
        desc: "Standby flight availability, flight trackers, and airline benefits.",
        icon: AirplanemodeActiveIcon,
        path: "/non-rev",
        glow: "rgba(14, 165, 233, 0.4)", // sky
        badge: "Flight Tool",
      },
      {
        title: "Flight Tracker",
        desc: "Save family flights and tap one to pull up its live status.",
        icon: FlightTakeoffIcon,
        path: "/flights",
        glow: "rgba(99, 102, 241, 0.4)", // indigo
        badge: "Live Status",
      },
      {
        title: "Cruise Planning",
        desc: "Compare cabin details, cruise ships, activities, and estimators.",
        icon: DirectionsBoatIcon,
        path: "/cruise-planning",
        glow: "rgba(6, 182, 212, 0.4)", // cyan
        badge: "2027 Planner",
      },
      {
        title: "Temple Tracker",
        desc: "Track visits, maps, and flight connections to temples.",
        icon: ChurchIcon,
        path: "/temples",
        glow: "rgba(245, 158, 11, 0.4)", // amber
        badge: "Temples & Airports",
      },
    ],
    [CATEGORIES.FAMILY]: [
      {
        title: "Chores",
        desc: "Daily chore lists, grab bag bonus tasks, and XP tracking.",
        icon: CleaningServicesIcon,
        path: "/chores",
        glow: "rgba(244, 63, 94, 0.4)", // rose
        badge: "Earn XP",
      },
      {
        title: "Mom Stories",
        desc: "Preserve and react to Mom's transcribed answers to family questions.",
        icon: FavoriteIcon,
        path: "/mom-stories",
        glow: "rgba(236, 72, 153, 0.4)", // pink
        badge: "Family Archives",
      },
      {
        title: "Family Timeline",
        desc: "Visual history of milestones, anniversaries, and birthdays.",
        icon: TimelineIcon,
        path: "/timeline",
        glow: "rgba(168, 85, 247, 0.4)", // purple
        badge: "Milestones",
      },
      {
        title: "Newsletter",
        desc: "Read the latest monthly newsletters and sibling updates.",
        icon: NewspaperIcon,
        path: currentNewsLetterPath,
        glow: "rgba(139, 92, 246, 0.4)", // violet
        badge: "Sibling News",
      },
      {
        title: "Photo Gallery",
        desc: "Browse through categorized family photo albums and eras.",
        icon: PhotoLibraryIcon,
        path: "/viewPictures",
        glow: "rgba(249, 115, 22, 0.4)", // orange
        badge: "Photo Journal",
      },
      {
        title: "Addresses",
        desc: "Contact information directory for the extended family.",
        icon: LocationOnIcon,
        path: "/addresses",
        glow: "rgba(20, 184, 166, 0.4)", // teal
        badge: "Directory",
      },
      {
        title: "CPR Pacing",
        desc: "Emergency chest-compression pacing metronome set to 110 BPM.",
        icon: MonitorHeartIcon,
        path: "/metronome",
        glow: "rgba(6, 182, 212, 0.4)", // cyan
        badge: "Emergency Tool",
      },
    ],
    [CATEGORIES.CHURCH]: [
      {
        title: "Samantha Mission",
        desc: "Updates, letters, maps, and photographs from Samantha's mission.",
        icon: PublicIcon,
        path: "https://samantha-s-mission.web.app/",
        glow: "rgba(14, 165, 233, 0.4)", // sky
        badge: "Mission Tracker",
      },
      {
        title: "Ancestor Converts",
        desc: "Interactive family convert mapping, locations, and history.",
        icon: AccountTreeIcon,
        path: "/ancestor-converts",
        glow: "rgba(99, 102, 241, 0.4)", // indigo
        badge: "Heritage Map",
      },
      {
        title: "Dymock History",
        desc: "Historical records, family history summaries, and documents.",
        icon: AutoStoriesIcon,
        path: "/dymock-history",
        glow: "rgba(234, 179, 8, 0.4)", // yellow
        badge: "Family Tree",
      },
    ],
  }

  // Animation configuration constants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    show: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100, damping: 15 } },
  }

  const categoryDetails = {
    [CATEGORIES.GAMES]: {
      name: "Games",
      color: "#00f2fe",
      glowColor: "rgba(0, 242, 254, 0.3)",
      bg: "linear-gradient(135deg, rgba(0, 242, 254, 0.05) 0%, rgba(79, 172, 254, 0.05) 100%)",
    },
    [CATEGORIES.TRAVEL]: {
      name: "Travel",
      color: "#ff9900",
      glowColor: "rgba(255, 153, 0, 0.3)",
      bg: "linear-gradient(135deg, rgba(255, 153, 0, 0.05) 0%, rgba(255, 91, 0, 0.05) 100%)",
    },
    [CATEGORIES.FAMILY]: {
      name: "Family",
      color: "#ff0844",
      glowColor: "rgba(255, 8, 68, 0.3)",
      bg: "linear-gradient(135deg, rgba(255, 8, 68, 0.05) 0%, rgba(255, 177, 153, 0.05) 100%)",
    },
    [CATEGORIES.CHURCH]: {
      name: "Church",
      color: "#a855f7",
      glowColor: "rgba(168, 85, 247, 0.3)",
      bg: "linear-gradient(135deg, rgba(168, 85, 247, 0.05) 0%, rgba(225, 0, 255, 0.05) 100%)",
    },
  }

  return (
    <SiteWrapper>
      <Helmet>
        <title>RyanDymock.com</title>
      </Helmet>

      {/* Embedded CSS for styling background pattern and keyframe glow movement */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes float-slow {
          0% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-15px) scale(1.05); }
          100% { transform: translateY(0px) scale(1); }
        }
        @keyframes float-reverse {
          0% { transform: translateY(0px) scale(1.05); }
          50% { transform: translateY(15px) scale(1); }
          100% { transform: translateY(0px) scale(1.05); }
        }
        .glowing-bg-blob-1 {
          animation: float-slow 12s ease-in-out infinite;
        }
        .glowing-bg-blob-2 {
          animation: float-reverse 15s ease-in-out infinite;
        }
        .custom-glass-card {
          position: relative;
          background: rgba(255, 255, 255, 0.02);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.07);
          border-radius: 24px;
          transition: border 0.3s, background 0.3s, box-shadow 0.3s;
          overflow: hidden;
        }
        .custom-glass-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 100% 0%, rgba(255,255,255,0.05) 0%, transparent 60%);
          pointer-events: none;
        }
      ` }} />

      {/* Main Full-Height Container with Dark Gradient Background */}
      <Box
        sx={{
          minHeight: "100vh",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          fontFamily: "'Kanit', sans-serif",
          justifyContent: "center",
        }}
      >

        {/* Content Area */}
        <Container
          maxWidth="lg"
          sx={{
            flex: 1,
            py: { xs: 4, md: 8 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <AnimatePresence mode="wait">
            {!activeCategory ? (
              /* CATEGORIES LIST SELECTION STATE */
              <motion.div
                key="categories"
                variants={containerVariants}
                initial="hidden"
                animate="show"
                exit={{ opacity: 0, y: -40, transition: { duration: 0.3 } }}
                style={{ width: "100%" }}
              >
                {/* Dashboard Header */}
                <Box sx={{ textAlign: "center", mb: { xs: 3, md: 8 } }}>
                  <motion.div variants={itemVariants}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 900,
                        letterSpacing: "-0.03em",
                        fontFamily: "'Playfair Display', serif",
                        background: "linear-gradient(to right, #fff 40%, rgba(255,255,255,0.7) 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
                      }}
                    >
                      RyanDymock.com
                    </Typography>
                  </motion.div>
                </Box>

                {/* 4 Large Tiles responsive grid */}
                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                  {Object.keys(categoryDetails).map((key) => {
                    const cat = categoryDetails[key]
                    return (
                      <Grid item xs={6} sm={6} md={3} key={key}>
                        <motion.div
                          variants={itemVariants}
                          whileHover={{
                            scale: 1.05,
                            y: -10,
                            boxShadow: `0px 20px 40px ${cat.glowColor}`,
                          }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Paper
                            onClick={() => setActiveCategory(key)}
                            className="custom-glass-card"
                            sx={{
                              height: { xs: "130px", md: "260px" },
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              cursor: "pointer",
                              textAlign: "center",
                              p: { xs: 2, md: 4 },
                              background: cat.bg,
                              "&:hover": {
                                borderColor: cat.color,
                              },
                            }}
                          >
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 800,
                                fontFamily: "'Kanit', sans-serif",
                                color: "#fff",
                                letterSpacing: "-0.01em",
                                fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.5rem" },
                                transition: "color 0.3s",
                                textShadow: "0 2px 10px rgba(0,0,0,0.5)",
                                position: "relative",
                                "&::after": {
                                  content: '""',
                                  position: "absolute",
                                  width: { xs: "24px", md: "40px" },
                                  height: { xs: "3px", md: "4px" },
                                  backgroundColor: cat.color,
                                  bottom: { xs: "-8px", md: "-12px" },
                                  left: { xs: "calc(50% - 12px)", md: "calc(50% - 20px)" },
                                  borderRadius: "2px",
                                },
                              }}
                            >
                              {cat.name}
                            </Typography>
                          </Paper>
                        </motion.div>
                      </Grid>
                    )
                  })}
                </Grid>
              </motion.div>
            ) : (
              /* SUB-PAGES "CARDS DEALT" STATE */
              <motion.div
                key="subpages"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                style={{ width: "100%" }}
              >
                {/* Back controls and Page heading */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: { xs: 5, md: 6 },
                    position: "relative",
                  }}
                >
                  {/* Glassmorphic Animated Back Button */}
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    <IconButton
                      onClick={() => setActiveCategory(null)}
                      sx={{
                        color: "#fff",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        background: "rgba(255, 255, 255, 0.03)",
                        backdropFilter: "blur(8px)",
                        mr: 3,
                        p: 1.5,
                        "&:hover": {
                          background: "rgba(255, 255, 255, 0.1)",
                          borderColor: categoryDetails[activeCategory].color,
                        },
                      }}
                    >
                      <ArrowBackIcon sx={{ fontSize: "20px" }} />
                    </IconButton>
                  </motion.div>

                  <Box>
                    <motion.div
                      initial={{ y: -10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.15 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: categoryDetails[activeCategory].color,
                          letterSpacing: "0.15em",
                          textTransform: "uppercase",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        PORTAL HUB
                      </Typography>
                    </motion.div>
                    <motion.div
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          fontFamily: "'Playfair Display', serif",
                          color: "#fff",
                        }}
                      >
                        {categoryDetails[activeCategory].name}
                      </Typography>
                    </motion.div>
                  </Box>
                </Box>

                {/* Sub-cards hand/grid layout with card dealing effect */}
                <Grid container spacing={3.5} justifyContent="center">
                  {subPages[activeCategory].map((page, index) => {
                    const IconComponent = page.icon
                    
                    // Card Deal effect configuration
                    // Cards fan out with a slight rotation based on index and deal from below
                    const cardsCount = subPages[activeCategory].length
                    const midIndex = (cardsCount - 1) / 2
                    const offset = index - midIndex
                    
                    // Uniform card deal settings (no rotation or curves)
                    const targetRotate = 0
                    const targetYOffset = 0
                    const targetXOffset = 0

                    return (
                      <Grid item xs={12} sm={6} md={4} key={page.title}>
                        <motion.div
                          initial={{
                            opacity: 0,
                            y: 80,
                            scale: 0.85,
                            rotate: 0,
                          }}
                          animate={{
                            opacity: 1,
                            y: 0,
                            x: 0,
                            scale: 1,
                            rotate: 0,
                          }}
                          transition={{
                            type: "spring",
                            stiffness: 100,
                            damping: 15,
                            delay: index * 0.05, // Staggered card entry
                          }}
                          whileHover={{
                            scale: 1.05,
                            y: -12,
                            rotate: 0,
                            zIndex: 10,
                            boxShadow: `0px 15px 35px ${page.glow}`,
                            transition: { type: "spring", stiffness: 300, damping: 20 },
                          }}
                          whileTap={{ scale: 0.97 }}
                          style={{
                            height: "100%",
                          }}
                        >
                          <Paper
                            onClick={() => {
                              if (page.path.startsWith("http")) {
                                window.open(page.path, "_blank", "noopener,noreferrer")
                              } else {
                                navigate(page.path)
                              }
                            }}
                            className="custom-glass-card"
                            sx={{
                              height: "230px",
                              p: 4.5,
                              cursor: "pointer",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                              "&:hover": {
                                borderColor: categoryDetails[activeCategory].color,
                                background: "rgba(255, 255, 255, 0.05)",
                              },
                            }}
                          >
                            <Box>
                              {/* Top card bar with icon and optional badge */}
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Box
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: "16px",
                                    bgcolor: "rgba(255, 255, 255, 0.03)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: categoryDetails[activeCategory].color,
                                  }}
                                >
                                  <IconComponent sx={{ fontSize: "24px" }} />
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: "20px",
                                    border: "1px solid rgba(255,255,255,0.05)",
                                    bgcolor: "rgba(255,255,255,0.02)",
                                    color: "rgba(255,255,255,0.4)",
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    letterSpacing: "0.05em",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {page.badge}
                                </Typography>
                              </Box>

                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 800,
                                  color: "#fff",
                                  fontFamily: "'Kanit', sans-serif",
                                  fontSize: "1.25rem",
                                  mb: 1,
                                }}
                              >
                                {page.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "rgba(255, 255, 255, 0.45)",
                                  fontSize: "0.85rem",
                                  lineHeight: 1.5,
                                  fontWeight: 300,
                                }}
                              >
                                {page.desc}
                              </Typography>
                            </Box>

                            {/* Launch CTA */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: categoryDetails[activeCategory].color,
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                                mt: 2,
                              }}
                            >
                              Launch Portal
                              <ChevronRightIcon sx={{ fontSize: "16px", ml: 0.5, transition: "transform 0.2s" }} className="arrow-icon" />
                            </Box>
                          </Paper>
                        </motion.div>
                      </Grid>
                    )
                  })}
                </Grid>
              </motion.div>
            )}
          </AnimatePresence>
        </Container>
      </Box>
    </SiteWrapper>
  )
}

export default NewHomePage
